import axios from "axios";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Layout from "../../components/layout/Layout";
import { formatDate } from "../../helpers/DateConversion";

export default function ViewOrder() {
  const location = useLocation();
  const data = location.state?.data;

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  if (!data) {
    return (
      <Layout>
        <div className="mt-10 text-center text-red-600">
          No order data found.
        </div>
      </Layout>
    );
  }

  const markAsDelivered = async () => {
    try {
      setLoading(true);
      const headers = {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("sansadToken")}`,
      };
      const formdata = new FormData();
      formdata.append("orderId", data._id);
      formdata.append("status", "Delivered");

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/order/update-status`,
        formdata,
        { headers }
      );

      if (response.data.statusCode === 200) {
        toast.success(response.data.message);
        navigate(-1);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <ToastContainer />
      <div className="bg-white md:p-10 p-4 rounded-xl shadow-md mt-8">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-medium">View Order</h2>
          {data.status === "Pending" && (
            <button
              className="border px-4 py-2 rounded-md text-sm"
              onClick={markAsDelivered}
              disabled={loading}
            >
              {loading ? "Updating..." : "Mark As Delivered"}
            </button>
          )}
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-y-6 gap-x-8 text-sm">

          <div>
            <h6 className="font-medium">Order ID</h6>
            <p className="mt-1">{data.orderId || "—"}</p>
          </div>

          <div>
            <h6 className="font-medium">Customer Name</h6>
            <p className="mt-1">{data.user?.name || "—"}</p>
          </div>

          <div>
            <h6 className="font-medium">Email</h6>
            <p className="mt-1">{data.user?.email || "—"}</p>
          </div>

          <div>
            <h6 className="font-medium">Product Name</h6>
            <p className="mt-1">{data?.product?.name || "—"}</p>
          </div>

          <div>
            <h6 className="font-medium">Quantity</h6>
            <p className="mt-1">{data.quantity || "—"}</p>
          </div>

          <div>
            <h6 className="font-medium">Amount</h6>
            <p className="mt-1">{data?.amount || "—"}</p>
          </div>


          <div>
            <h6 className="font-medium">Delivery Charge</h6>
            <p className="mt-1">{data?.deliveryCharge || "—"}</p>
          </div>


          <div>
            <h6 className="font-medium">Emergency Charge</h6>
            <p className="mt-1">{data?.emergencyCharge || "—"}</p>
          </div>


          <div>
            <h6 className="font-medium">GST</h6>
            <p className="mt-1">{data?.gst || "—"}</p>
          </div>


          <div>
            <h6 className="font-medium">Total Amount</h6>
            <p className="mt-1">{data?.totalAmount || (data?.price ?? 0) + (data?.deliveryCharge ?? 0)}</p>
          </div>

          <div>
            <h6 className="font-medium">Payment Method</h6>
            <p className="mt-1">{data?.paymentMethod || "Online"}</p>
          </div>

          {data?.plan && (
            <div>
              <h6 className="font-medium">Plan</h6>
              <p className="mt-1">{data?.plan.name || "—"}</p>
            </div>
          )}

          <div>
            <h6 className="font-medium">Is Emergency</h6>
            <p className="mt-1">{data?.isEmergency ? "Yes" : "No"}</p>
          </div>

          <div>
            <h6 className="font-medium">Status</h6>
            <p className="mt-1">{data?.status || "—"}</p>
          </div>

          <div>
            <h6 className="font-medium">Created At</h6>
            <p className="mt-1">{formatDate(data?.createdAt) || "—"}</p>
          </div>

          <div>
            <h6 className="font-medium">Pickup Time</h6>
            <p className="mt-1">{data.pickupTime || "—"}</p>
          </div>

          <div>
            <h6 className="font-medium">Delivery Time</h6>
            <p className="mt-1">{data.deliveryTime || "—"}</p>
          </div>

          <div className="md:col-span-2">
            <h6 className="font-medium">Special Instruction</h6>
            <p className="mt-1">{data.specialInstruction || "—"}</p>
          </div>

          {data?.address ? (
            <div className="md:col-span-2">
              <h6 className="font-medium">Address</h6>
              <p className="mt-1">
                {data?.address
                  ? `${data?.address.fullAddress || ""}, ${data?.address.city || ""
                  }, ${data?.address.state || ""} - ${data?.address.pincode || ""
                  }`
                  : "—"}
              </p>
            </div>
          ) : (
            <>
              <div className="md:col-span-2">
                <h6 className="font-medium">Address</h6>
                <p className="py-1 text-sm">
                  {data?.deliveryAddress?.fullAddress +
                    ", " +
                    data?.deliveryAddress?.city +
                    ", " +
                    data?.deliveryAddress?.state +
                    ", " +
                    data?.deliveryAddress?.pincode}
                </p>
                <p className=" text-sm">
                  {data?.pickupAddress?.fullAddress +
                    ", " +
                    data?.pickupAddress?.city +
                    ", " +
                    data?.pickupAddress?.state +
                    ", " +
                    data?.pickupAddress?.pincode}
                </p>
              </div>
            </>
          )}

          {Array.isArray(data.accessories) && data.accessories.length > 0 && (
            <div className="md:col-span-2">
              <h6 className="font-medium mb-2">Accessories</h6>
              <ul className="list-disc ml-6 space-y-1">
                {data.accessories.map((acc, i) => (
                  <li key={i}>
                    {acc.detail?.name || "Accessory"} - Qty: {acc.quantity}, Amount: ₹{acc.amount * acc.quantity}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
